/**
 * Support Ticket WebSocket Service (Singleton)
 * Manages WebSocket connection for real-time ticket updates
 */

import { getToken } from '../utils/tokenStorage';

// Get store dynamically to avoid circular dependency
const getStore = () => {
  return require('../../store').store;
};

const getWsBaseUrl = () => {
  if (process.env.REACT_APP_WS_BASE_URL) {
    return process.env.REACT_APP_WS_BASE_URL;
  }
  if (window.location.hostname === 'fleetcamagile.com') {
    return 'wss://fleetcamagile.com';
  }
  return 'ws://localhost:8000';
};

class TicketService {
  constructor() {
    this.ws = null;
    this.reconnectInterval = null;
    this.reconnectAttempts = 0;
    this.heartbeatInterval = null;
    this.userId = null;
    this.isConnected = false;
  }

  initializeWebSocket(userId) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      console.log('[TicketService] Already connected');
      return;
    }

    if (!userId) return;

    this.userId = userId;

    try {
      const WS_BASE_URL = getWsBaseUrl();
      const token = getToken();
      const wsUrl = `${WS_BASE_URL}/ws/tickets/?token=${token || ''}`;
      console.log('[TicketService] Connecting to:', wsUrl);

      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('[TicketService] Connected successfully');
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.startHeartbeat();

        const store = getStore();
        if (store && store.dispatch) {
          store.dispatch({ type: 'tickets/setConnected', payload: true });
        }
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (error) {
          console.error('[TicketService] Error parsing message:', error);
        }
      };

      this.ws.onerror = (error) => {
        console.error('[TicketService] WebSocket error:', error);
        this.isConnected = false;
      };

      this.ws.onclose = () => {
        console.log('[TicketService] Connection closed');
        this.isConnected = false;
        this.stopHeartbeat();

        const store = getStore();
        if (store && store.dispatch) {
          store.dispatch({ type: 'tickets/setConnected', payload: false });
        }

        this.scheduleReconnect();
      };
    } catch (error) {
      console.error('[TicketService] Connection error:', error);
      this.isConnected = false;
    }
  }

  handleMessage(data) {
    const store = getStore();
    if (!store || !store.dispatch) return;

    switch (data.type) {
      case 'initial_data':
        console.log(`[TicketService] Initial data: ${data.tickets?.length} tickets`);
        store.dispatch({
          type: 'tickets/setTickets',
          payload: data.tickets || [],
        });
        break;

      case 'ticket_update':
        if (data.action === 'created') {
          console.log('[TicketService] New ticket:', data.ticket.subject);
          store.dispatch({
            type: 'tickets/addTicket',
            payload: data.ticket,
          });
        } else if (data.action === 'updated') {
          store.dispatch({
            type: 'tickets/updateTicket',
            payload: data.ticket,
          });
        }
        break;

      case 'heartbeat':
      case 'pong':
        break;

      default:
        console.log('[TicketService] Unknown message type:', data.type);
    }
  }

  scheduleReconnect() {
    if (this.reconnectInterval || !this.userId) return;

    this.reconnectAttempts++;
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);

    console.log(`[TicketService] Reconnecting in ${delay / 1000}s`);

    this.reconnectInterval = setTimeout(() => {
      this.reconnectInterval = null;
      this.initializeWebSocket(this.userId);
    }, delay);
  }

  startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    if (this.reconnectInterval) {
      clearTimeout(this.reconnectInterval);
      this.reconnectInterval = null;
    }

    this.stopHeartbeat();
    this.isConnected = false;
    this.userId = null;

    console.log('[TicketService] Disconnected');
  }

  getConnectionStatus() {
    return {
      connected: this.isConnected,
      readyState: this.ws?.readyState,
    };
  }
}

// Create singleton instance
const ticketService = new TicketService();

export default ticketService;
